import { Bubble } from "@ant-design/x";
import { Typography } from "antd";
import { useT } from "../i18n/context";
import type { ChatMessage, CodeBlockRenderer } from "../types";
import CostInfoLine from "./CostInfoLine";
import MarkdownRenderer from "./MarkdownRenderer";
import ThinkingBlock from "./ThinkingBlock";
import ToolCallDrawer from "./ToolCallDrawer";

const { Text } = Typography;

interface MessageBubbleProps {
  msg: ChatMessage;
  agentName: string;
  isLast: boolean;
  codeBlockRenderers?: Record<string, CodeBlockRenderer>;
  onCodeBlockRenderError?: (language: string, cause: Error) => void;
}

export default function MessageBubble({
  msg,
  agentName,
  isLast,
  codeBlockRenderers,
  onCodeBlockRenderError,
}: MessageBubbleProps) {
  const t = useT();

  const isUser = msg.role === "user";
  const speakerLabel = isUser ? t("chat.you") : agentName;

  return (
    <div
      style={{
        padding: "0 20px",
        marginBottom: 16,
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
      }}
    >
      <Text type="secondary" style={{ fontSize: 11, marginBottom: 4, color: "var(--lude-im-text-secondary)" }}>
        {speakerLabel}
      </Text>

      {msg.thinking && (
        <div style={{ alignSelf: "stretch", maxWidth: "75%" }}>
          <ThinkingBlock
            content={msg.thinking}
            isActive={isLast && !msg.content && !msg.costInfo}
          />
        </div>
      )}

      {msg.toolCalls && msg.toolCalls.length > 0 && (
        <div style={{ alignSelf: "stretch", maxWidth: "75%", marginBottom: 8 }}>
          <ToolCallDrawer toolCalls={msg.toolCalls} />
        </div>
      )}

      {msg.content && (
        isUser ? (
          <div style={{ maxWidth: "75%", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <div
              style={{
                background: "var(--lude-im-accent)",
                color: "#fff",
                borderRadius: "var(--lude-im-radius-bubble)",
                padding: "10px 14px",
                fontSize: 13,
                wordBreak: "break-word",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {msg.content}
            </div>
            {msg.deliveryStatus && (
              <span style={{ fontSize: 10, marginTop: 2, color: "var(--lude-im-text-secondary)", opacity: 0.7 }}>
                {msg.deliveryStatus === "delivered" ? "✓✓" : msg.deliveryStatus === "received" ? "✓" : "⏳"}
              </span>
            )}
          </div>
        ) : (
          <div style={{ maxWidth: "75%", minWidth: 0 }}>
            <Bubble
              placement="start"
              content={
                msg.streaming ? (
                  msg.content
                ) : (
                  <MarkdownRenderer
                    content={msg.content}
                    codeBlockRenderers={codeBlockRenderers}
                    onCodeBlockRenderError={onCodeBlockRenderError}
                  />
                )
              }
              styles={{
                content: {
                  background: "var(--lude-im-bg-tertiary)",
                  color: "var(--lude-im-text-primary)",
                  borderRadius: "var(--lude-im-radius-bubble)",
                  fontSize: 13,
                  wordBreak: "break-word",
                  whiteSpace: msg.streaming ? "pre-wrap" : undefined,
                },
              }}
            />
          </div>
        )
      )}

      {msg.costInfo && <CostInfoLine text={msg.costInfo} />}
    </div>
  );
}
